import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import VerticalLayout from "components/VerticalLayout";

// Redux actions
import { grantRouteAccess, denyRouteAccess, redirectToLogin } from "../../store/auth/routeValidation/actions";
import { validateToken } from "../../store/auth/tokenValidation/actions";

// Components
import TokenExpirationModal from "../../components/TokenExpirationModal";

const EnhancedAuthMiddleware = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Redux selectors
  const routeValidation = useSelector(state => state.routeValidation);
  const tokenValidation = useSelector(state => state.tokenValidation);
  

  // Monitor localStorage changes from the same tab
  useEffect(() => {
    let lastAuthStatus = getAuthStatus();
    
    const checkLocalStorage = () => {
      const currentAuthStatus = getAuthStatus();
      
      if (lastAuthStatus.hasToken !== currentAuthStatus.hasToken) {
        console.log('EnhancedAuthMiddleware: Auth status changed from same tab', {
          oldStatus: lastAuthStatus,
          newStatus: currentAuthStatus
        });
        
        lastAuthStatus = currentAuthStatus;
        
        // If token was removed and we have access, clear it
        if (!currentAuthStatus.hasToken && routeValidation.hasAccess) {
          console.log('EnhancedAuthMiddleware: Token removed from same tab, clearing access');
          dispatch(denyRouteAccess(location.pathname, 'token_removed', 'Authentication token was removed'));
        }
      }
    };
    
    // Check every 10 minutes
    const interval = setInterval(checkLocalStorage, 10 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [routeValidation.hasAccess, location.pathname, dispatch]);

  // Public routes that don't require authentication
  const PUBLIC_ROUTES = [
    '/login',
    '/forgot-password',
    '/register',
    '/pages-404',
    '/pages-500',
    '/pages-maintenance',
    '/pages-comingsoon'
  ];

  const isPublicRoute = (pathname) => {
    return PUBLIC_ROUTES.some(route => pathname.startsWith(route));
  };

  // Helper function to check authentication status
  const getAuthStatus = () => {
    const authUser = localStorage.getItem('authUser');
    const pocketbaseAuth = localStorage.getItem('pocketbase_auth');
    
    // Both tokens are required for proper authentication
    // pocketbase_auth is required for SDK calls
    // authUser is required for the application state
    if (!pocketbaseAuth || !authUser) {
      return { hasToken: false, token: null, source: null };
    }
    
    // Check pocketbase_auth first (primary authentication - required for SDK calls)
    if (pocketbaseAuth) {
      try {
        const parsedAuth = JSON.parse(pocketbaseAuth);
        if (parsedAuth.token) {
          return { hasToken: true, token: parsedAuth.token, source: 'pocketbase_auth' };
        }
      } catch (error) {
        console.error('Error parsing pocketbase_auth:', error);
      }
    }
    
    // Check authUser as fallback
    if (authUser) {
      try {
        const parsedAuth = JSON.parse(authUser);
        if (parsedAuth.token) {
          return { hasToken: true, token: parsedAuth.token, source: 'authUser' };
        }
      } catch (error) {
        console.error('Error parsing authUser:', error);
      }
    }
    
    // No valid token found
    return { hasToken: false, token: null, source: null };
  };

  // Single validation effect for mount and route changes
  useEffect(() => {
    const pathname = location.pathname;

    // Skip validation for public routes
    if (isPublicRoute(pathname)) {
      return;
    }

    // Check authentication status using helper function
    const authStatus = getAuthStatus();
    
    if (authStatus.hasToken) {
      // We have a token, grant immediate access
      dispatch(grantRouteAccess(pathname, 'existing_token', authStatus.token));
      return;
    }

    // No token found, redirect to login
    dispatch(redirectToLogin(pathname, navigate));
  }, [location.pathname, dispatch, navigate]);

  // Force validation on every navigation (more aggressive check)
  useEffect(() => {
    const pathname = location.pathname;
    
    // Skip validation for public routes
    if (isPublicRoute(pathname)) {
      return;
    }

    // Force validation on every navigation
    const authStatus = getAuthStatus();
    
    if (!authStatus.hasToken) {
      dispatch(redirectToLogin(pathname, navigate));
    }
  }, [location.pathname, dispatch, navigate]);

  // Effect that runs on every render to check localStorage
  useEffect(() => {
    const pathname = location.pathname;
    
    // Skip validation for public routes
    if (isPublicRoute(pathname)) {
      return;
    }

    // Check authentication status using helper function
    const authStatus = getAuthStatus();
    
    if (authStatus.hasToken && !routeValidation.hasAccess) {
      dispatch(grantRouteAccess(pathname, 'existing_token', authStatus.token));
    } else if (!authStatus.hasToken && routeValidation.hasAccess) {
      // Token was removed but we still have access, clear it
      dispatch(denyRouteAccess(pathname, 'token_removed', 'Authentication token was removed'));
    }
  }); // No dependencies, runs on every render

  // Additional effect to handle localStorage changes after login
  useEffect(() => {
    const pathname = location.pathname;
    
    // Skip validation for public routes
    if (isPublicRoute(pathname)) {
      return;
    }

    // Check if we have a valid token in localStorage
    const authUser = localStorage.getItem('authUser');
    
    if (authUser && !routeValidation.hasAccess) {
      try {
        const parsedAuth = JSON.parse(authUser);
        if (parsedAuth.token) {
          dispatch(grantRouteAccess(pathname, 'existing_token', parsedAuth.token));
          dispatch(validateToken(parsedAuth.token));
        }
      } catch (error) {
        console.error('Error parsing authUser after login:', error);
      }
    }
  }, [routeValidation.hasAccess, location.pathname, dispatch]);

  // Handle route access denial
  useEffect(() => {
    if (routeValidation.error && !routeValidation.hasAccess) {
      dispatch(redirectToLogin(location.pathname, navigate));
    }
  }, [routeValidation.error, routeValidation.hasAccess, routeValidation.isChecking, dispatch, location.pathname, navigate]);

  // Effect to handle state reset after login
  useEffect(() => {
    // State reset handled automatically by Redux
  }, [routeValidation.hasAccess, routeValidation.error]);

  // Effect to handle missing authentication tokens (force redirect to login)
  useEffect(() => {
    const pathname = location.pathname;
    
    // Skip validation for public routes
    if (isPublicRoute(pathname)) {
      return;
    }

    const authStatus = getAuthStatus();
    
    if (!authStatus.hasToken && !routeValidation.isChecking) {
      // Clear any existing error state
      dispatch({ type: 'CLEAR_ROUTE_ERROR' });
      dispatch(redirectToLogin(pathname, navigate));
    }
  }, [location.pathname, routeValidation.isChecking, dispatch, navigate]);

  // Listen for localStorage changes (when token is removed)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if ((e.key === 'authUser' || e.key === 'pocketbase_auth') && !e.newValue) {
        // Token was removed, clear access and redirect
        dispatch(denyRouteAccess(location.pathname, 'token_removed', 'Authentication token was removed'));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [dispatch, location.pathname]);

  // Check for token removal on every render
  useEffect(() => {
    const authStatus = getAuthStatus();
    if (!authStatus.hasToken && routeValidation.hasAccess) {
      // Token was removed but we still have access, clear it
      dispatch(denyRouteAccess(location.pathname, 'token_removed', 'Authentication token was removed'));
    }
  });

  // Handle token expiration warning
  const handleTokenRenewal = () => {
    // For now, just hide the warning
    // In a real implementation, this would trigger a refresh token flow
    dispatch({ type: 'HIDE_EXPIRATION_WARNING' });
  };

  const handleTokenExpiration = () => {
    // User chose not to renew, redirect to login
    dispatch(redirectToLogin(location.pathname, navigate));
  };

  const Layout = VerticalLayout;

  // Show loading state while checking authentication
  if (routeValidation.isChecking) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Validating authentication...</span>
        </div>
      </div>
    );
  }

  // Redirect to login if access is denied
  if (!routeValidation.hasAccess && routeValidation.error) {
    // Dispatch redirect action to trigger the saga
    dispatch(redirectToLogin(location.pathname, navigate));
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Redirecting to login...</span>
        </div>
      </div>
    );
  }

  // Show token expiration warning modal
  if (tokenValidation.showWarning) {
    return (
      <React.Fragment>
        <Layout>{props.children}</Layout>
        <TokenExpirationModal
          timeRemaining={tokenValidation.timeRemaining}
          onRenew={handleTokenRenewal}
          onExpire={handleTokenExpiration}
        />
      </React.Fragment>
    );
  }

  // Render protected content
  return (
    <React.Fragment>
      <Layout>{props.children}</Layout>
    </React.Fragment>
  );
};

export default EnhancedAuthMiddleware;
